import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, asyncScheduler } from 'rxjs';
import { distinctUntilChanged, map, observeOn } from 'rxjs/operators';

export interface SubjectItem {
  name: string;
  hierarchy: string;
}

export interface UIState {
  currentProblemId: number | string;
  subjectList: SubjectItem[];
  activity: 'practice' | 'test' | 'finalTest' | 'million' | '1vs1' | '_';
  activitySubjects: string;
  type: 'math' | 'text';
  score: number;
}

const initialState: UIState = {
  currentProblemId: 0,
  subjectList: [],
  activity: '_',
  activitySubjects: '_',
  type: 'math',
  score: 100,
};

@Injectable({
  providedIn: 'root'
})
export class UIStateService {
  private state: BehaviorSubject<UIState>;
  public readonly state$: Observable<UIState>;

  constructor() {
    this.state = new BehaviorSubject<UIState>({ ...initialState });
    // Use asyncScheduler to prevent ExpressionChangedAfterItHasBeenCheckedError
    this.state$ = this.state.asObservable().pipe(observeOn(asyncScheduler));
  }

  // Selectors
  get currentProblemId$(): Observable<number | string> {
    return this.state$.pipe(
      map(s => s.currentProblemId),
      distinctUntilChanged()
    );
  }

  get subjectList$(): Observable<SubjectItem[]> {
    return this.state$.pipe(
      map(s => s.subjectList),
      distinctUntilChanged()
    );
  }

  get activity$(): Observable<UIState['activity']> {
    return this.state$.pipe(
      map(s => s.activity),
      distinctUntilChanged()
    );
  }

  get activitySubjects$(): Observable<string> {
    return this.state$.pipe(
      map(s => s.activitySubjects),
      distinctUntilChanged()
    );
  }

  get type$(): Observable<'math' | 'text'> {
    return this.state$.pipe(
      map(s => s.type),
      distinctUntilChanged()
    );
  }

  get score$(): Observable<number> {
    return this.state$.pipe(
      map(s => s.score),
      distinctUntilChanged()
    );
  }

  // Getters for current values
  get currentProblemId(): number | string {
    return this.state.getValue().currentProblemId;
  }

  get subjectList(): SubjectItem[] {
    return this.state.getValue().subjectList;
  }

  get activity(): UIState['activity'] {
    return this.state.getValue().activity;
  }

  get type(): 'math' | 'text' {
    return this.state.getValue().type;
  }

  get score(): number {
    return this.state.getValue().score;
  }

  // Mutators
  setCurrentProblemId(id: number | string): void {
    this.updateState({ currentProblemId: id });
  }

  setSubjectList(subjectList: SubjectItem[]): void {
    this.updateState({ subjectList });
  }

  setActivity(activity: UIState['activity']): void {
    this.updateState({ activity });
  }

  setActivitySubjects(activitySubjects: string): void {
    this.updateState({ activitySubjects });
  }

  setType(type: 'math' | 'text'): void {
    this.updateState({ type });
  }

  setScore(score: number): void {
    this.updateState({ score });
  }

  // Reset to initial state
  reset(): void {
    this.state.next({ ...initialState });
  }

  private updateState(partial: Partial<UIState>): void {
    this.state.next({ ...this.state.getValue(), ...partial });
  }
}
